import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import FloatingCTA from '../components/FloatingCTA';
import BookingForm from '../components/BookingForm';
import { CheckIcon, ArrowRightIcon, StarIcon } from '../components/Icons';

const SLIDES = [
  { id:1, tag:'Back & Neck Pain', headline:'Relief from back and neck pain, at home.', sub:'DHA-licensed physiotherapists come to you — no commute, no waiting room.', photo:'/hero-back.jpg', link:'/back-pain' },
  { id:2, tag:'Sports Injury', headline:'Get back to full strength, faster.', sub:'Expert sports rehabilitation delivered to your door across Dubai.', photo:'/hero-sports.jpg', link:'/sports-injury' },
  { id:3, tag:'Post-Surgery Recovery', headline:'Recover safely without leaving home.', sub:'Professional rehabilitation after knee, hip, or spinal surgery.', photo:'/hero-surgery.jpg', link:'/post-surgery' },
  { id:4, tag:'Elderly Care & Mobility', headline:'Gentle, specialist care for elderly loved ones.', sub:'Improving mobility, reducing pain, and preventing falls — at home.', photo:'/hero-elderly.jpg', link:'/elderly-care' },
];

export default function Home() {
  const [current, setCurrent] = useState(0);
  const slide = SLIDES[current];

  useEffect(() => {
    const t = setInterval(() => setCurrent(p => (p+1) % SLIDES.length), 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ background:'var(--cream)', minHeight:'100vh' }}>
      <Header />

      {/* HERO */}
      <section style={{ position:'relative', minHeight:'92vh', display:'flex', alignItems:'center', paddingTop:'68px', transition:'all 0.6s' }}>
        <div className="hero-photo" style={{ backgroundImage:`url('${slide.photo}')`, transition:'background-image 0.8s' }} />
        <div className="hero-overlay" />
        <div className="container" style={{ position:'relative', zIndex:1, padding:'72px 24px 100px' }}>
          <div style={{ maxWidth:'640px' }}>
            <div className="section-tag-white">{slide.tag}</div>
            <h1 style={{ fontSize:'clamp(36px,5.5vw,60px)', color:'white', marginBottom:'18px', lineHeight:'1.15' }}>
              {slide.headline}
            </h1>
            <p style={{ fontSize:'18px', color:'rgba(255,255,255,0.82)', marginBottom:'36px', maxWidth:'520px', lineHeight:'1.75' }}>
              {slide.sub}
            </p>
            <div style={{ display:'flex', gap:'14px', flexWrap:'wrap' }}>
              <Link to={slide.link} className="btn-primary" style={{ fontSize:'16px', padding:'15px 32px' }}>
                Request a Home Visit
              </Link>
              <Link to={slide.link} className="btn-outline" style={{ fontSize:'16px', padding:'15px 32px' }}>
                Learn more <ArrowRightIcon size={15} color="white" />
              </Link>
            </div>
            <div style={{ display:'flex', gap:'22px', marginTop:'44px', flexWrap:'wrap' }}>
              {['DHA Licensed','Same-day available','Female therapists available'].map(t => (
                <div key={t} style={{ display:'flex', alignItems:'center', gap:'6px', color:'rgba(255,255,255,0.75)', fontSize:'13px', fontWeight:'500' }}>
                  <CheckIcon size={13} color="var(--teal)" />{t}
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Slide dots */}
        <div style={{ position:'absolute', bottom:'32px', left:'50%', transform:'translateX(-50%)', display:'flex', gap:'8px', zIndex:2 }}>
          {SLIDES.map((_,i) => (
            <button key={i} onClick={() => setCurrent(i)} style={{ width:i===current?'24px':'8px', height:'8px', borderRadius:'50px', background:i===current?'white':'rgba(255,255,255,0.35)', border:'none', cursor:'pointer', transition:'all 0.3s' }} />
          ))}
        </div>
      </section>

      {/* CONDITIONS GRID */}
      <section style={{ background:'var(--white)', padding:'80px 24px' }}>
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:'48px' }}>
            <div className="section-tag">What we treat</div>
            <h2 style={{ fontSize:'40px', marginBottom:'12px' }}>Choose your condition</h2>
            <p style={{ color:'var(--text-light)', fontSize:'16px' }}>Each service is tailored to your specific needs</p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:'20px' }}>
            {[
              { title:'Back & Neck Pain', desc:'Desk pain, sciatica, postural issues, chronic back pain.', link:'/back-pain', photo:'/hero-back.jpg' },
              { title:'Sports Injury', desc:'ACL, rotator cuff, Achilles, muscle tears, fracture rehab.', link:'/sports-injury', photo:'/hero-sports.jpg' },
              { title:'Post-Surgery Rehab', desc:'Knee, hip, spinal, and shoulder surgery recovery.', link:'/post-surgery', photo:'/hero-surgery.jpg' },
              { title:'Elderly Care', desc:'Balance, falls prevention, arthritis, mobility support.', link:'/elderly-care', photo:'/hero-elderly.jpg' },
            ].map((c,i) => (
              <Link key={i} to={c.link} style={{
                display:'block', borderRadius:'var(--radius-lg)', overflow:'hidden',
                border:'1px solid var(--cream-dark)', textDecoration:'none',
                transition:'transform 0.2s, box-shadow 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}
              >
                <div style={{ height:'180px', backgroundImage:`url('${c.photo}')`, backgroundSize:'cover', backgroundPosition:'center', position:'relative' }}>
                  <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top,rgba(15,25,40,0.6),transparent)' }} />
                </div>
                <div style={{ padding:'20px' }}>
                  <div style={{ fontWeight:'600', fontSize:'16px', color:'var(--navy)', marginBottom:'6px' }}>{c.title}</div>
                  <div style={{ fontSize:'13px', color:'var(--text-light)', lineHeight:'1.6', marginBottom:'12px' }}>{c.desc}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:'6px', color:'var(--teal)', fontSize:'13px', fontWeight:'600' }}>
                    Learn more <ArrowRightIcon size={13} color="var(--teal)" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ background:'var(--cream)', padding:'80px 24px' }}>
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:'52px' }}>
            <div className="section-tag">Simple process</div>
            <h2 style={{ fontSize:'40px' }}>How it works</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:'24px' }}>
            {[
              { n:'01', title:'Tell us your condition', desc:'Call, WhatsApp, or fill the form. Takes under a minute. We ask only what we need to match you with the right therapist.' },
              { n:'02', title:'We confirm your appointment', desc:'Our team contacts you to confirm the therapist, time, and location. We match therapists by specialisation and area.' },
              { n:'03', title:'Therapist arrives at your door', desc:'Your DHA-licensed therapist arrives on time with all equipment. One-on-one, professional care in your own space.' },
            ].map((s,i) => (
              <div key={i} style={{ background:'white', padding:'36px 28px', borderRadius:'var(--radius-lg)', border:'1px solid var(--cream-dark)', position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:'-8px', right:'20px', fontSize:'70px', fontWeight:'800', color:'var(--teal-light)', fontFamily:'Playfair Display,serif', lineHeight:1, userSelect:'none' }}>
                  {s.n}
                </div>
                <h3 style={{ fontSize:'19px', marginBottom:'10px', fontFamily:'DM Sans,sans-serif', fontWeight:'600', color:'var(--navy)' }}>{s.title}</h3>
                <p style={{ color:'var(--text-light)', lineHeight:'1.7', fontSize:'15px' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY MOTIONCARE */}
      <section style={{ background:'var(--navy)', padding:'80px 24px' }}>
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:'52px' }}>
            <div className="section-tag-white">Why choose us</div>
            <h2 style={{ fontSize:'40px', color:'white' }}>What sets MotionCare apart</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'16px' }}>
            {[
              { title:'DHA Licensed', desc:'Every therapist is licensed by Dubai Health Authority — no exceptions.' },
              { title:'All Equipment Included', desc:'Your therapist brings everything. No need to buy or prepare anything.' },
              { title:'One-on-One Sessions', desc:'Dedicated therapist for your full session. No waiting room, no distractions.' },
              { title:'Female Therapists', desc:'Female therapists available on request, for every service we offer.' },
              { title:'Multilingual', desc:'Arabic, English, Hindi, Filipino — we speak your language.' },
              { title:'Sessions from AED 250', desc:'Transparent pricing. No hidden fees. Invoice provided after every session.' },
            ].map((f,i) => (
              <div key={i} style={{ background:'rgba(255,255,255,0.06)', padding:'26px 22px', borderRadius:'var(--radius-lg)', border:'1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ display:'flex', gap:'10px', alignItems:'flex-start', marginBottom:'10px' }}>
                  <CheckIcon size={16} color="var(--teal)" />
                  <div style={{ color:'white', fontSize:'15px', fontWeight:'600' }}>{f.title}</div>
                </div>
                <p style={{ color:'rgba(255,255,255,0.58)', fontSize:'13px', lineHeight:'1.65', paddingLeft:'26px' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section style={{ background:'var(--cream)', padding:'80px 24px' }}>
        <div className="container">
          <div style={{ maxWidth:'620px', margin:'0 auto' }}>
            <div style={{ textAlign:'center', marginBottom:'40px' }}>
              <div className="section-tag">Book in 2 minutes</div>
              <h2 style={{ fontSize:'38px', marginBottom:'12px' }}>Request your home visit</h2>
              <p style={{ color:'var(--text-light)', fontSize:'16px' }}>
                We will call you back to confirm your appointment and match you with the right therapist.
              </p>
            </div>
            <div style={{ background:'white', padding:'40px', borderRadius:'var(--radius-lg)', border:'1px solid var(--cream-dark)', boxShadow:'var(--shadow-sm)' }}>
              <BookingForm />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={{ background:'var(--white)', padding:'80px 24px' }}>
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:'48px' }}>
            <div className="section-tag">Patient experiences</div>
            <h2 style={{ fontSize:'40px' }}>What our patients say</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:'20px' }}>
            {[
              { name:'Sarah M.', loc:'Dubai Marina', text:'I had back pain and couldn\'t drive to a clinic. MotionCare sent a therapist the same day. The home visits made recovery so much easier than I expected.', cond:'Back Pain' },
              { name:'Ahmed R.', loc:'Downtown Dubai', text:'The therapist helped me follow my recovery plan with confidence after my knee surgery. Excellent professional care and I didn\'t have to leave home once.', cond:'Post-Surgery' },
              { name:'Priya K.', loc:'JLT', text:'Booked for my mother. The therapist was patient, gentle, and explained everything clearly. Mum is more mobile now than she has been in years.', cond:'Elderly Care' },
            ].map((t,i) => (
              <div key={i} style={{ background:'var(--cream)', padding:'28px', borderRadius:'var(--radius-lg)', border:'1px solid var(--cream-dark)' }}>
                <div style={{ display:'flex', gap:'2px', marginBottom:'14px' }}>
                  {[1,2,3,4,5].map(s => <StarIcon key={s} size={15} />)}
                </div>
                <p style={{ color:'var(--text)', lineHeight:'1.75', marginBottom:'18px', fontSize:'15px', fontStyle:'italic' }}>
                  "{t.text}"
                </p>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontWeight:'600', fontSize:'14px' }}>{t.name}</div>
                    <div style={{ color:'var(--text-light)', fontSize:'12px' }}>{t.loc}</div>
                  </div>
                  <div style={{ background:'var(--teal-light)', color:'var(--teal-dark)', fontSize:'11px', fontWeight:'600', padding:'4px 10px', borderRadius:'50px' }}>
                    {t.cond}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background:'var(--navy)', padding:'56px 24px 32px' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'40px', marginBottom:'40px' }}>
            <div>
              <div style={{ fontFamily:'Playfair Display,serif', fontWeight:'700', fontSize:'20px', color:'white', marginBottom:'12px' }}>
                MotionCare<span style={{ color:'var(--teal)' }}> Physio</span>
              </div>
              <p style={{ fontSize:'14px', color:'rgba(255,255,255,0.55)', lineHeight:'1.7', maxWidth:'220px' }}>
                Professional Healing in the Comfort of Home. DHA-licensed home physiotherapy across Dubai.
              </p>
            </div>
            <div>
              <div style={{ color:'white', fontWeight:'600', marginBottom:'14px', fontSize:'14px' }}>Services</div>
              {[['Back & Neck Pain','/back-pain'],['Sports Injury','/sports-injury'],['Post-Surgery Rehab','/post-surgery'],['Elderly Care','/elderly-care']].map(([l,h]) => (
                <Link key={l} to={h} style={{ display:'block', fontSize:'14px', color:'rgba(255,255,255,0.55)', marginBottom:'8px' }}>{l}</Link>
              ))}
            </div>
            <div>
              <div style={{ color:'white', fontWeight:'600', marginBottom:'14px', fontSize:'14px' }}>Areas We Cover</div>
              {['Dubai Marina & JLT','Downtown & DIFC','Jumeirah & Umm Suqeim','Business Bay','Palm Jumeirah'].map(a => (
                <div key={a} style={{ fontSize:'14px', color:'rgba(255,255,255,0.55)', marginBottom:'8px' }}>{a}</div>
              ))}
            </div>
            <div>
              <div style={{ color:'white', fontWeight:'600', marginBottom:'14px', fontSize:'14px' }}>Contact Us</div>
              <a href="tel:+97100000000" style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'14px', color:'rgba(255,255,255,0.55)', marginBottom:'12px' }}>+971 XX XXX XXXX</a>
              <a href="https://wa.me/97100000000" target="_blank" rel="noopener noreferrer" style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'14px', color:'#25D366', marginBottom:'12px' }}>WhatsApp Us</a>
              <a href="mailto:hello@motioncare.ae" style={{ display:'block', fontSize:'14px', color:'rgba(255,255,255,0.55)' }}>hello@motioncare.ae</a>
            </div>
          </div>
          <div style={{ borderTop:'1px solid rgba(255,255,255,0.1)', paddingTop:'24px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
            <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)' }}>© 2026 MotionCare Physio LLC. All rights reserved.</div>
            <div style={{ display:'flex', gap:'20px', fontSize:'13px' }}>
              {['Privacy Policy','Terms of Service','Refund Policy'].map(l => (
                <a key={l} href="#" style={{ color:'rgba(255,255,255,0.4)' }}>{l}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <FloatingCTA />
    </div>
  );
}
